// Email Log screens. Pure: data in, HTML out.
// Words: EMAIL_SENDER_WORDS.md. Mockup: MOCKUP_EMAIL_SENDER.html (both approved 2026-10-10).
// Send Test Email added on Robert's yes, 2026-10-10 (not in the mockup).
// Read only. Server code queues every email; the sender logs it.

import { closedAtText } from "./jsp-close";
import { LOG_LIMIT, emailPillOf, emailRowLine, emailRowsFor, routeLabel, type EmailRow } from "./email";
import { esc, ident } from "./training-screens";

function row(label: string, value: string): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e"></div></div>`;
}

export function emailPill(r: Pick<EmailRow, "status" | "route">): string {
  const p = emailPillOf(r);
  return `<span class="pill ${p.tone}">${esc(p.label)}</span>`;
}

export interface TestState {
  busy: boolean;
  error: string;
  note: string;
}

/** The list: site first (All Sites at the top), then All or Failed, newest first. */
export function emailLog(all: EmailRow[], siteNames: Record<string, string>, site: string, failedOnly: boolean, test: TestState): string {
  const shown = emailRowsFor(all, site, failedOnly);
  const sites = Object.keys(siteNames).sort();
  const options = [
    `<option value="all"${site === "all" ? " selected" : ""}>All Sites</option>`,
    ...sites.map((s) => `<option value="${esc(s)}"${site === s ? " selected" : ""}>${esc(`${s} · ${siteNames[s]}`)}</option>`),
  ].join("");
  const chips = `<a class="em-chip${failedOnly ? "" : " on"}" href="#/email-log/${esc(site)}">All</a><a class="em-chip${failedOnly ? " on" : ""}" href="#/email-log/${esc(site)}/failed">Failed</a>`;
  const rows = shown
    .map(
      (r) => `<a class="ar gl-row em-row" href="#/email-log/m/${encodeURIComponent(r.emailId)}">
        <span class="t"><b>${esc(r.subject || "(no subject)")}</b><small>${esc(emailRowLine(r))}</small>${emailPill(r)}</span></a>`,
    )
    .join("");
  const empty = failedOnly ? "No failed emails here." : "No emails here yet.";
  const title = site === "all" ? "All Sites" : siteNames[site] ?? `Site ${site}`;
  const sub = `${shown.length} ${shown.length === 1 ? "email" : "emails"}${all.length >= LOG_LIMIT ? ` · newest ${LOG_LIMIT} read` : ""}`;
  return `${ident("EM", `Email Log · ${title}`, sub)}
    <div class="em-ctrl">
      <label class="site-pick" for="em-site">Site <select id="em-site" data-act="em-site">${options}</select></label>
      <span class="em-chips">${chips}</span>
    </div>
    <div class="box">${rows || `<div class="empty">${empty}</div>`}</div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Send Test Email</b><small>Two emails, one through each route. On dev they come to you.</small></div>
      <button class="prim" type="button" data-act="em-test"${test.busy ? " disabled" : ""}>${test.busy ? "Sending" : "Send Test Email"}</button></div>
    </div>
    ${test.error ? `<div class="note red" role="status">${esc(test.error)}</div>` : ""}
    ${test.note ? `<div class="note em-note" role="status">${esc(test.note)}</div>` : ""}
    <p class="ro">Grey Sent is normal. Amber: the backup carried it. Red: nobody got it.</p>`;
}

/** One email: as sent, the route, who it went to, every try. */
export function emailRecord(r: EmailRow, siteName: string, backHref: string): string {
  const to = r.to.length ? r.to.map((x) => esc(x.name ? `${x.name} · ${x.email}` : x.email)).join("<br>") : '<span class="muted">None</span>';
  const tries = r.tries.length
    ? r.tries
        .map((t, i) => `<div>${i + 1}. ${esc(t.route === "main" ? "Main" : "Backup")} · ${esc(closedAtText(t.at))} · ${t.ok ? "Sent" : esc(`Refused: ${t.error}`)}</div>`)
        .join("")
    : '<span class="muted">No tries</span>';
  const frame = r.html
    ? `<iframe class="em-frame" title="${esc(r.subject)}" sandbox="" referrerpolicy="no-referrer" srcdoc="${esc(r.html)}"></iframe>`
    : `<div class="empty">${r.status === "queued" || r.status === "sending" ? "Still on its way. Open again in a moment." : "Nothing was built to show."}</div>`;
  return `<a class="back" href="${esc(backHref)}">Email Log</a>
    ${ident("EM", r.subject || "(no subject)", [siteName, closedAtText(r.createdAt)].filter(Boolean).join(" · "))}
    ${r.status === "failed" && r.error ? `<div class="note red">${esc(r.error)}</div>` : ""}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>How It Went</b><small>${esc([r.reason, r.recordId].filter(Boolean).join(", "))}</small></div>${emailPill(r)}</div>
      <div class="rows">
        ${row("To", to)}
        ${r.devLine ? row("Sent to", `${r.sentTo.map(esc).join("<br>")}<br><span class="muted">${esc(r.devLine)}</span>`) : ""}
        ${row("Route", esc(routeLabel(r.route)))}
        ${row("Reply-to", r.replyTo ? esc(r.replyTo) : '<span class="muted">None set</span>')}
        ${row("Sent", r.sentAt ? esc(closedAtText(r.sentAt)) : '<span class="muted">Not sent</span>')}
        ${row("Tries", tries)}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>The Email As Sent</b><small>Plain-text copy below it</small></div></div>
      ${frame}
      ${r.text ? `<details class="em-text"><summary>Plain Text</summary><pre>${esc(r.text)}</pre></details>` : ""}
    </div>
    <p class="ro">Read only. The sender logs every email.</p>`;
}
